import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { API, axios } from "@/api";
import { ThumbsUp, Meh, ThumbsDown, Star, X, Send, Heart } from "lucide-react";
import { toast } from "sonner";
import { useCopyBank } from "@/hooks/useCopyBank";
import BrandCard from "@/components/shared/BrandCard";

const STEPS = { WORTH: "worth", RATING: "rating", NUDGE: "nudge", FEEDBACK: "feedback", DONE: "done" };

export default function PostClaimFeedback({ isOpen, onClose, businessId, businessName, logoUrl }) {
  const { getText } = useCopyBank();
  const [step, setStep] = useState(STEPS.WORTH);
  const [worthIt, setWorthIt] = useState(null);
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [feedbackText, setFeedbackText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [pointsEarned, setPointsEarned] = useState(0);
  const [brandCardOpen, setBrandCardOpen] = useState(false);

  if (!isOpen) return null;

  const submitFeedback = async (fields) => {
    try {
      const res = await axios.post(`${API}/offer-feedback`, {
        business_id: businessId,
        ...fields,
      });
      if (res.data?.already_submitted) {
        toast.info("You already rated this offer");
      }
      if (res.data?.points_awarded) {
        setPointsEarned(res.data.points_awarded);
      }
      return res.data;
    } catch (e) {
      console.error(e);
      // Fall back to tracker if offer-feedback fails
      try {
        await axios.post(`${API}/tracker`, { business_id: businessId, ...fields });
      } catch {}
    }
  };

  const handleWorth = async (value) => {
    setWorthIt(value);
    setStep(STEPS.RATING);
  };

  const handleRating = async (stars) => {
    setRating(stars);
    setSubmitting(true);
    await submitFeedback({ rating: stars, worth_it: worthIt });
    setSubmitting(false);
    toast.success(`Thanks for rating! +25 pts`);
    handleFinish();
  };

  const handleSkipRating = () => {
    setStep(STEPS.NUDGE);
  };

  const handleNudgeSkip = () => {
    // Still save worth_it even without stars
    if (worthIt) submitFeedback({ rating: 3, worth_it: worthIt });
    handleFinish();
  };

  const handleNudgeFeedback = () => {
    setStep(STEPS.FEEDBACK);
  };

  const handleSubmitFeedback = async () => {
    if (!feedbackText.trim()) return;
    setSubmitting(true);
    await submitFeedback({
      rating: rating || 3,
      worth_it: worthIt,
      comment: feedbackText.trim(),
    });
    setSubmitting(false);
    toast.success("Feedback submitted — thank you! +25 pts");
    handleFinish();
  };

  const handleFinish = () => {
    setStep(STEPS.DONE);
    setTimeout(() => {
      onClose();
      setStep(STEPS.WORTH);
      setWorthIt(null);
      setRating(0);
      setHoverRating(0);
      setFeedbackText("");
      setPointsEarned(0);
    }, 300);
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[200] bg-black/40 backdrop-blur-sm flex items-center justify-center px-5"
        onClick={handleFinish}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          transition={{ type: "spring", damping: 25, stiffness: 300 }}
          className="w-full max-w-sm bg-white rounded-2xl shadow-2xl overflow-hidden"
          onClick={(e) => e.stopPropagation()}
          data-testid="post-claim-modal"
        >
          {/* Header */}
          <div className="px-5 pt-5 pb-2 flex items-center justify-between">
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Quick Feedback</p>
            <button onClick={handleFinish} className="w-7 h-7 rounded-full bg-slate-100 flex items-center justify-center" data-testid="feedback-close">
              <X className="w-3.5 h-3.5 text-slate-400" />
            </button>
          </div>

          {/* STEP 1: Worth It? */}
          {step === STEPS.WORTH && (
            <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="px-5 pb-6">
              <h3 className="text-base font-bold text-slate-800 mb-1" data-testid="feedback-worth-title">
                Was <span onClick={() => setBrandCardOpen(true)} className="cursor-default" data-testid="brand-tap-claim">{businessName}</span> worth it?
              </h3>
              <p className="text-xs text-slate-400 mb-5">Your honest take helps the next person decide</p>
              <div className="flex justify-center gap-4">
                {[
                  { value: "yes", icon: ThumbsUp, label: "Worth it", bg: "bg-emerald-50 hover:bg-emerald-100 border-emerald-200", ic: "text-emerald-500" },
                  { value: "meh", icon: Meh, label: "It was okay", bg: "bg-amber-50 hover:bg-amber-100 border-amber-200", ic: "text-amber-500" },
                  { value: "no", icon: ThumbsDown, label: "Not really", bg: "bg-red-50 hover:bg-red-100 border-red-200", ic: "text-red-400" },
                ].map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => handleWorth(opt.value)}
                    className={`flex flex-col items-center gap-2 px-5 py-4 rounded-xl border ${opt.bg} transition-all active:scale-95`}
                    data-testid={`feedback-worth-${opt.value}`}
                  >
                    <opt.icon className={`w-7 h-7 ${opt.ic}`} />
                    <span className="text-[10px] font-semibold text-slate-600">{opt.label}</span>
                  </button>
                ))}
              </div>
            </motion.div>
          )}

          {/* STEP 2: Star Rating */}
          {step === STEPS.RATING && (
            <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="px-5 pb-6">
              <h3 className="text-base font-bold text-slate-800 mb-1" data-testid="feedback-rating-title">
                {getText("rate_offer")}
              </h3>
              <p className="text-xs text-slate-400 mb-5">Tap a star — earns 25 pts</p>
              <div className="flex justify-center gap-2 mb-5">
                {[1, 2, 3, 4, 5].map((s) => (
                  <button
                    key={s}
                    onClick={() => handleRating(s)}
                    onMouseEnter={() => setHoverRating(s)}
                    onMouseLeave={() => setHoverRating(0)}
                    className="p-1 transition-transform hover:scale-110 active:scale-95"
                    data-testid={`feedback-star-${s}`}
                  >
                    <Star
                      className={`w-9 h-9 transition-colors ${
                        s <= (hoverRating || rating) ? "text-amber-400 fill-amber-400" : "text-slate-200"
                      }`}
                    />
                  </button>
                ))}
              </div>
              <button
                onClick={handleSkipRating}
                className="w-full py-2.5 text-xs text-slate-400 font-medium hover:text-slate-600 transition-colors"
                data-testid="feedback-skip-rating"
              >
                Skip
              </button>
            </motion.div>
          )}

          {/* STEP 3: Nudge */}
          {step === STEPS.NUDGE && (
            <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="px-5 pb-6">
              <div className="flex justify-center mb-4">
                <div className="w-12 h-12 rounded-full bg-sky-50 flex items-center justify-center">
                  <Heart className="w-6 h-6 text-sky-400" />
                </div>
              </div>
              <h3 className="text-base font-bold text-slate-800 text-center mb-2" data-testid="feedback-nudge-title">
                Your voice matters
              </h3>
              <p className="text-xs text-slate-500 text-center leading-relaxed mb-5">
                Your feedback helps the next birthday celebrant make a better choice. Even a quick note goes a long way.
              </p>
              <button
                onClick={handleNudgeFeedback}
                className="w-full py-3 rounded-xl bg-sky-500 text-white text-sm font-semibold shadow-md shadow-sky-500/20 hover:bg-sky-600 transition-colors mb-2"
                data-testid="feedback-nudge-yes"
              >
                I'll share a quick thought
              </button>
              <button
                onClick={handleNudgeSkip}
                className="w-full py-2.5 text-xs text-slate-400 font-medium hover:text-slate-600 transition-colors"
                data-testid="feedback-nudge-skip"
              >
                Maybe next time
              </button>
            </motion.div>
          )}

          {/* STEP 4: Written Feedback */}
          {step === STEPS.FEEDBACK && (
            <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="px-5 pb-5">
              <h3 className="text-base font-bold text-slate-800 mb-1" data-testid="feedback-text-title">
                Tell us about your experience
              </h3>
              <p className="text-xs text-slate-400 mb-4">This helps us — and could reach the business</p>
              <textarea
                value={feedbackText}
                onChange={(e) => setFeedbackText(e.target.value)}
                placeholder="How was the experience? Was the staff friendly? Did you get what was advertised?"
                className="w-full h-24 px-3 py-2.5 rounded-xl border border-slate-200 text-sm text-slate-700 placeholder:text-slate-300 resize-none focus:outline-none focus:ring-2 focus:ring-sky-400/40 focus:border-sky-400"
                data-testid="feedback-textarea"
              />
              <div className="flex gap-2 mt-3">
                <button
                  onClick={handleFinish}
                  className="flex-1 py-3 rounded-xl text-xs text-slate-400 font-medium hover:bg-slate-50 transition-colors"
                  data-testid="feedback-text-skip"
                >
                  Skip
                </button>
                <button
                  onClick={handleSubmitFeedback}
                  disabled={!feedbackText.trim() || submitting}
                  className="flex-1 py-3 rounded-xl bg-sky-500 text-white text-sm font-semibold shadow-md shadow-sky-500/20 hover:bg-sky-600 disabled:opacity-50 transition-colors flex items-center justify-center gap-1.5"
                  data-testid="feedback-text-submit"
                >
                  <Send className="w-3.5 h-3.5" />
                  Submit
                </button>
              </div>
            </motion.div>
          )}
        </motion.div>
      </motion.div>
      <BrandCard
        isOpen={brandCardOpen}
        onClose={() => setBrandCardOpen(false)}
        businessId={businessId}
        businessName={businessName}
        logoUrl={logoUrl}
      />
    </AnimatePresence>
  );
}
